import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface RematriculacionRequest {
  id: string | undefined,

  matricula_ck: {
    MTR001_N_MATRICULA: string,
    MTR001_ID_DELEGACION: string,
    MTR001_D_NOMBRE: string,
    MTR001_D_APELLIDO: string,
  },

  motivo_rechazo: string;
  numero_resolucion: string;
  fecha_resolucion: string;

  state_id: string;

  attachs: RequestAttachment[];
}

export interface RematriculacionRequestAttachs {
  tipo: string;
  tipo_archivo: string;
  url: string;
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

function View() {
  const { id } = useParams();

  const [data, setData] = useState<RematriculacionRequest>({
    id: id,

    matricula_ck: {
      MTR001_N_MATRICULA: '',
      MTR001_ID_DELEGACION: '',
      MTR001_D_NOMBRE: '',
      MTR001_D_APELLIDO: '',
    },

    motivo_rechazo: '',
    numero_resolucion: '',
    fecha_resolucion: '',

    state_id: '',

    attachs: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [resolucionFile, setResolucionFile] = useState<File | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const ATTACHMENTS_CONFIG = [
    { key: 'titulo_adjunto', alt: 'Título Universitario' },
    { key: 'foto_carnet', alt: 'Foto Carnet' },
    { key: 'fotocopia_dni_frente', alt: 'Fotocopia DNI Frente' },
    { key: 'fotocopia_dni_dorso', alt: 'Fotocopia DNI Dorso' },
    { key: 'antecedentes_penales', alt: 'Antecedentes Penales' },
    { key: 'certificado_extranjero', alt: 'Certificado Extranjero' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('rematriculacion', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleFinalizarRematriculacion = async () => {
    if (!resolucionFile) {
      alert('Adjunte el archivo PDF con la resolución para finalizar la rematriculación.');
      return;
    }

    setIsLoading(true);

    if (id) {
      try {
        const headers: HeadersInit = {
          ...(localStorage.getItem('accessToken') && {
            Authorization: localStorage.getItem('accessToken') as string,
          }),
        };

        const formData = new FormData();
        formData.append('numero_resolucion', data.numero_resolucion ?? '');
        formData.append('fecha_resolucion', data.fecha_resolucion);
        formData.append('archivo_resolucion', resolucionFile);

        const res = await fetch(`${config.baseUrl}/rematriculacion/${ id }/crear-cokiba`, {
          method: 'POST',
          headers,
          body: formData,
        });

        const response = await res.json();

        if (response?.status !== 'ok') {
          console.info(response);

          if (response.payload?.message) {
            alert(response.payload.message);
            setIsLoading(false);
            return;
          }

          alert('Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.');
          return;
        }

        alert('Se guardó correctamente la resolución.', () => {
          window.location.reload();
        });
      } catch (err) {
        console.error(err);
        alert('Ha ocurrido un error al crear la matrícula, por favor intente nuevamente.');
      }
    }
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Solicitud de Rematriculacion #{ id }</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              setResolucionFile={ setResolucionFile }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onAccept={ false }
              onReject={ false }
              onFinalizarRematriculacion={ handleFinalizarRematriculacion }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default View;
